import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUpload, FaTrash, FaImage, FaTimes, FaGripVertical, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Image Item Component
const SortableImageItem = ({ id, index, imageUrl, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group"
    >
      <div className="relative">
        <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-primary-500 transition-colors">
          <img
            src={imageUrl}
            alt={`Vehicle image ${index + 1}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = '/assets/assets/images/business-shop-sign.webp';
            }}
          />
          {/* Position indicator */}
          <div className="absolute top-2 left-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs font-semibold">
            #{index + 1}
          </div>
          
          {/* Drag handle - this is the draggable area */}
          <div
            {...attributes}
            {...listeners}
            className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white p-2 rounded cursor-grab active:cursor-grabbing hover:bg-opacity-80 transition-colors"
            title="Drag to reorder"
          >
            <FaGripVertical className="w-4 h-4" />
          </div>
        </div>
        
        {/* Delete button */}
        <button
          onClick={() => onDelete(index)}
          className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-20"
          title="Remove image"
        >
          <FaTimes className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

const ImageUpload = ({ images = [], onImagesChange, vehicleId, vehicleInfo }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({}); // 'converting' | 'uploading' | 'done'
  const [dragOver, setDragOver] = useState(false);

  const validateImage = (file) => {
    const maxSize = 25 * 1024 * 1024; // 25MB (we'll compress anyway)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'];
    
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }
    
    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 25MB' };
    }
    
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Invalid file type. Please upload JPEG, PNG, WebP, GIF, or BMP files.' };
    }
    
    return { valid: true };
  };

  // Convert image to JPEG format with max dimensions of 1600x1600 (maintains aspect ratio)
  // Using JPEG because Google Vehicle Listing Ads reject WebP
  const convertToJpeg = async (file, maxWidth = 1600, maxHeight = 1600, quality = 0.90) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const aspectRatio = width / height;
          
          if (width > height) {
            // Landscape: limit by width
            if (width > maxWidth) {
              width = maxWidth;
              height = Math.round(width / aspectRatio);
            }
          } else {
            // Portrait or square: limit by height
            if (height > maxHeight) {
              height = maxHeight;
              width = Math.round(height * aspectRatio);
            }
          }
          
          // Double-check the other dimension
          if (width > maxWidth) {
            width = maxWidth;
            height = Math.round(width / aspectRatio);
          }
          if (height > maxHeight) {
            height = maxHeight;
            width = Math.round(height * aspectRatio);
          }
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        // Fill white background (JPEG doesn't support transparency)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        // Enable high-quality image scaling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const jpgFileName = file.name.replace(/\.[^.]+$/, '.jpg');
              const jpgFile = new File([blob], jpgFileName, { type: 'image/jpeg' });
              console.log(`Image converted: ${file.name} (${(file.size / 1024).toFixed(1)}KB) → ${jpgFileName} (${(jpgFile.size / 1024).toFixed(1)}KB), Dimensions: ${img.width}x${img.height} → ${width}x${height}`);
              resolve(jpgFile);
            } else {
              reject(new Error('Failed to convert image to JPEG'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for conversion'));
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const uploadImage = async (file) => {
    try {
      const timestamp = Date.now();
      // Build SEO-friendly filename: year-make-model-N.jpg
      const slugParts = [];
      if (vehicleInfo?.year) slugParts.push(vehicleInfo.year);
      if (vehicleInfo?.make) slugParts.push(vehicleInfo.make.toLowerCase().replace(/\s+/g, '-'));
      if (vehicleInfo?.model) slugParts.push(vehicleInfo.model.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-'));
      const slugBase = slugParts.length > 0 ? slugParts.join('-') : (vehicleId || 'temp');
      const imgIndex = images.length + 1;
      const fileExt = file.type === 'image/jpeg' ? 'jpg' : file.name.split('.').pop();
      const fileName = `${slugBase}-${imgIndex}-${timestamp}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('vehicle-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });
        
      if (error) throw error;
      
      const { data: { publicUrl } } = supabase.storage
        .from('vehicle-images')
        .getPublicUrl(fileName);
      
      return {
        success: true,
        url: publicUrl,
        fileName: fileName,
        path: data.path
      };
    } catch (error) {
      console.error('Image upload error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    const newImages = [...images];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validation = validateImage(file);
      
      if (!validation.valid) {
        alert(`Error with ${file.name}: ${validation.error}`);
        continue;
      }

      try {
        setUploadProgress(prev => ({ ...prev, [file.name]: 10 }));
        setUploadStatus(prev => ({ ...prev, [file.name]: 'converting' }));
        
        // Convert to JPEG with max 1600x1600 dimensions
        let processedFile;
        let conversionInfo = null;
        try {
          const originalSize = file.size;
          processedFile = await convertToJpeg(file, 1600, 1600, 0.90);
          conversionInfo = {
            originalSize,
            newSize: processedFile.size,
            savings: Math.round((1 - processedFile.size / originalSize) * 100)
          };
          setUploadProgress(prev => ({ ...prev, [file.name]: 50 }));
        } catch (conversionError) {
          console.warn(`JPEG conversion failed for ${file.name}, uploading original:`, conversionError);
          processedFile = file; // Fallback to original if conversion fails
        }
        
        setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
        const result = await uploadImage(processedFile);
        
        if (result.success) {
          newImages.push(result.url);
          setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
          setUploadStatus(prev => ({ 
            ...prev, 
            [file.name]: conversionInfo ? `done-${conversionInfo.savings}` : 'done' 
          }));
        } else {
          alert(`Failed to upload ${file.name}: ${result.error}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        alert(`Failed to upload ${file.name}`);
      }
      
      // Clear progress after a delay
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
        setUploadStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[file.name];
          return newStatus;
        });
      }, 3000);
    }

    onImagesChange(newImages);
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = images.findIndex((img) => img === active.id);
      const newIndex = images.findIndex((img) => img === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newImages = arrayMove(images, oldIndex, newIndex);
        onImagesChange(newImages);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[15px] font-semibold text-adm-text-hi">Vehicle Images</h3>
        <span className="text-sm text-gray-500">{images.length} image(s)</span>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver 
            ? 'border-primary-500 bg-primary-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <FaImage className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <div className="space-y-2">
          <p className="text-[15px] font-semibold text-adm-text-hi">
            Drop image files here or click to browse
          </p>
          <p className="text-sm text-gray-500">
            JPEG, PNG, WebP, GIF, BMP up to 25MB
          </p>
          <p className="text-xs text-green-600 font-medium">
            ✨ Auto-optimized to JPEG (max 1600×1600)
          </p>
        </div>
        
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          id="image-upload"
        />
        
        <label
          htmlFor="image-upload"
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 cursor-pointer"
        >
          <FaUpload className="mr-2" />
          Browse Images
        </label>
      </div>

      {/* Upload Progress */}
      <AnimatePresence>
        {Object.keys(uploadProgress).map((fileName) => {
          const status = uploadStatus[fileName] || 'preparing';
          const isConverting = status === 'converting';
          const isUploading = status === 'uploading';
          const isDone = status.startsWith('done');
          const savings = isDone && status.includes('-') ? status.split('-')[1] : null;
          
          return (
            <motion.div
              key={fileName}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`rounded-lg p-3 ${isDone ? 'bg-green-50' : 'bg-gray-50'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {isConverting && (
                    <div className="flex items-center gap-1.5 text-blue-600">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="text-xs font-medium">Converting to JPEG...</span>
                    </div>
                  )}
                  {isUploading && (
                    <div className="flex items-center gap-1.5 text-primary-600">
                      <FaUpload className="h-3.5 w-3.5 animate-pulse" />
                      <span className="text-xs font-medium">Uploading...</span>
                    </div>
                  )}
                  {isDone && (
                    <div className="flex items-center gap-1.5 text-green-600">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-xs font-medium">
                        Optimized! {savings && <span className="text-green-700">({savings}% smaller)</span>}
                      </span>
                    </div>
                  )}
                </div>
                <span className="text-sm text-gray-500">{uploadProgress[fileName]}%</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 truncate flex-1">{fileName}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <motion.div
                  className={`h-2 rounded-full ${isDone ? 'bg-green-500' : isConverting ? 'bg-blue-500' : 'bg-primary-600'}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress[fileName]}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Current Images - With Drag and Drop */}
      {images.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Current Images</h4>
            <p className="text-sm text-gray-500">Drag images to reorder</p>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={images}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((imageUrl, index) => (
                  <SortableImageItem
                    key={imageUrl}
                    id={imageUrl}
                    index={index}
                    imageUrl={imageUrl}
                    onDelete={removeImage}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {uploading && (
        <div className="text-center py-4">
          <div className="inline-flex items-center text-primary-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
            Uploading images...
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;